<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import { useVuelidate } from "@vuelidate/core";
import { helpers, required, requiredIf } from "@vuelidate/validators";
import ModalBase from "@/components/modal/ModalBase.vue";
import ArgonSnackbar from "@/components/ArgonSnackbar.vue";
import ArgonInput from "@/components/ArgonInput.vue";
import ArgonSelect from "@/components/ArgonSelect.vue";
import ArgonLoadingButton from "@/components/ArgonLoadingButton.vue";
import ArgonSwitch from "@/components/ArgonSwitch.vue";
import { eventStore } from "@/_store/ttmmEventStore";
import { accountStore } from "@/_store/accountStore";
import { errorUtils } from "@/utils";

const props = defineProps({
  eventId: {
    type: [String, Number],
    default: null,
  },
});

const emit = defineEmits(["close", "success"]);
const vuexStore = useStore();
const eventsStore = eventStore();
const accountsStore = accountStore();
const bootstrap = vuexStore.state.bootstrap;
const modalRef = ref(null);
const snackbar = ref(null);
const disableButton = ref(false);
const incidenceOnlyScopes = ["RECOGIDA", "ENTREGA"];

const typeOptions = ref([
  { label: "Transito", value: "TRANSITO" },
  { label: "Reparto", value: "EN_REPARTO" },
  { label: "Incidencia", value: "INCIDENCIA" }
]);

const scopeOptions = ref([
  { label: "Recogida", value: "RECOGIDA" },
  { label: "Entrega", value: "ENTREGA" },
  { label: "Milla intermedia", value: "MILLA_INTERMEDIA" }
]);

const entityOptions = ref([
    { label: "Parada", value: "PARADA" },
    { label: "Paquete", value: "PAQUETE" }
]);

const createDefaultForm = () => ({
  type: "",
  scope: "",
  entity: "",
  code: "",
  description: "",
  do_not_send: false,
});

const form = ref(createDefaultForm());
const isEdit = computed(() => !!props.eventId);
const modalTitle = computed(() => (isEdit.value ? "Modificar" : "Nuevo"));
const showEntity = computed(() => form.value.scope !== "" && form.value.scope !== "MILLA_INTERMEDIA");
const showDoNotSend = computed(() => 
  accountsStore.account?.beepacket_enabled &&
  form.value.scope !== "" &&
  form.value.scope !== "MILLA_INTERMEDIA"
);
const isIncidenceOnlyScope = computed(() =>
  incidenceOnlyScopes.includes(form.value.scope)
);
const filteredTypeOptions = computed(() => {
  if (!form.value.scope) {
    return [];
  }
  if (isIncidenceOnlyScope.value) {
    return typeOptions.value.filter((option) => option.value === "INCIDENCIA");
  }
  return typeOptions.value;
});

const rules = computed(() => ({
  type: {
    required: helpers.withMessage(
      "Tipo es requerido",
      requiredIf(() => form.value.scope !== "")
    ),
  },
  scope: {
    required: helpers.withMessage("Ámbito es requerido", required),
  },
  entity: {
    required: helpers.withMessage(
      "Entidad es requerida",
      requiredIf(() => showEntity.value)
    ),
  },
  code: {
    required: helpers.withMessage("Codigo es requerido", required),
  },
  description: {
    required: helpers.withMessage("Descripcion es requerida", required),
  },
}));

const v$ = useVuelidate(rules, form);

function fillForm(eventItem) {
  form.value = {
    type: eventItem?.type || "",
    scope: eventItem?.scope || "",
    entity: eventItem?.entity || "",
    code: eventItem?.code || "",
    description: eventItem?.description || "",
    do_not_send: eventItem?.do_not_send ?? false,
  };
}

watch(
  () => props.eventId,
  (value) => {
    disableButton.value = false;
    fillForm(null);
    v$.value.$reset();
    if (value) {
      eventsStore.getEventById(value);
    }
  },
  { immediate: true }
);

watch(
  () => eventsStore.event,
  (value) => {
    if (!props.eventId || !value) return;
    if (String(value._id || "") !== String(props.eventId)) return;
    fillForm(value);
    v$.value.$reset();
  }
);

watch(
  () => form.value.scope,
  (scope) => {
    if (isIncidenceOnlyScope.value) {
      form.value.type = "INCIDENCIA";
      return;
    }

    if (!scope) {
      form.value.type = "";
    }
  }
);

watch(
  () => eventsStore.eventCreated,
  (value) => {
    if (!value) return;
    disableButton.value = false;
    modalRef.value.closeModal();
    emit("success");
  }
);

watch(
  () => eventsStore.eventUpdated,
  (value) => {
    if (!value) return;
    disableButton.value = false;
    modalRef.value.closeModal();
    emit("success");
  }
);

watch(
  () => eventsStore.error,
  (value) => {
    if (!value) return;
    disableButton.value = false;
    snackbar.value = {
      title: "Error",
      message: errorUtils.getErrorMessageHtml(value),
      type: "danger",
      color: "white",
    };
  }
);

onMounted(() => { 
  if (!accountsStore.account) {
    accountsStore.getAccount().catch(() => null);
  }

  const modal = new bootstrap.Modal(document.getElementById("modalEvent"), {
    keyboard: true,
    backdrop: "static",
  });
  modal.show();
});

function closeModal() {
  emit("close");
}

const closeSnackbar = () => {
  snackbar.value = null;
};

async function validate() {
  await v$.value.$validate();
  return v$.value.$errors;
}

async function saveEvent() {
  const errors = await validate();

  if (errors.length > 0) {
    let message = "";
    for (const error of errors) {
      if (!message.includes(`<li>${error.$message}</li>`)) {
        message = `${message}<li>${error.$message}</li>`;
      }
    }

    snackbar.value = {
      title: "Error",
      message: `<ul>${message}</ul>`,
      type: "danger",
      color: "white",
    };
    return;
  }

  disableButton.value = true;
  const payload = {
    type: form.value.type,
    scope: form.value.scope,
    entity: showEntity.value ? form.value.entity : "PAQUETE",
    code: form.value.code,
    description: form.value.description,
  };
  if (showDoNotSend.value) {
    payload.do_not_send = form.value.do_not_send;
  }
  if (isEdit.value) {
    eventsStore.updateEvent(payload, props.eventId);
    return;
  }
  eventsStore.createEvent(payload);
}
</script>

<template>
  <modal-base id="modalEvent" ref="modalRef" width="35rem" @hide="closeModal">
    <template #header>
      <h6 class="modal-title mb-0">
        {{ modalTitle }}
      </h6>
    </template>

    <template #body>
      <div class="modal-form--wide-labels">
        
        <div class="row modal-form-row">
          <div class="modal-form-label">
            <label for="event-scope" class="col-form-label required"
              >Ámbito</label
            >
          </div>
          <div class="col">
            <argon-select
              id="event-scope"
              v-model="form.scope"
              name-label="label"
              name-value="value"
              :options="scopeOptions"
              :error="v$.scope.$error"
            />
          </div>
        </div>

        <div class="row modal-form-row">
          <div class="modal-form-label">
            <label for="event-type" class="col-form-label required">Tipo evento</label>
          </div>
          <div class="col">
            <argon-select
              id="event-type"
              v-model="form.type"
              name-label="label"
              name-value="value"
              :options="filteredTypeOptions"
              :error="v$.type.$error"
            />
          </div>
        </div>

       

        <div v-if="showEntity" class="row modal-form-row">
          <div class="modal-form-label">
            <label for="event-entity" class="col-form-label required"
              >Entidad</label
            >
          </div>
          <div class="col">
            <argon-select
              id="event-entity"
              v-model="form.entity"
              name-label="label"
              name-value="value"
              :options="entityOptions"
              :error="v$.entity.$error"
            />
          </div>
        </div>

        <div class="row modal-form-row">
          <div class="modal-form-label">
            <label for="event-code" class="col-form-label required">Código</label>
          </div>
          <div class="col">
            <argon-input
              id="event-code"
              v-model="form.code"
              :max-length="15"
              :error="v$.code.$error"
              :is-required="true"
            />
          </div>
        </div>

        <div class="row modal-form-row">
          <div class="modal-form-label">
            <label for="event-description" class="col-form-label required"
              >Descripción</label
            >
          </div>
          <div class="col">
            <argon-input
              id="event-description"
              v-model="form.description"
              :max-length="40"
              :error="v$.description.$error"
              :is-required="true"
            />
          </div>
        </div>

        <div v-if="showDoNotSend" class="row modal-form-row">
          <div class="col">
            <argon-switch
              id="event-do-not-send"
              v-model="form.do_not_send"
              name="do_not_send"
            >
              No comunicar a terceros
            </argon-switch>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <argon-loading-button
        class="btn bg-gradient-primary btn-xs"
        color="primary"
        variant="gradient"
        :label="disableButton ? 'Guardando...' : 'Guardar'"
        :loading="disableButton"
        :disable-button="disableButton"
        @click="saveEvent"
      />
    </template>

    <div class="position-fixed top-0 end-0 z-index-2 p-3">
      <argon-snackbar
        v-if="snackbar"
        :title="snackbar.title"
        :description="snackbar.message"
        :icon="{ component: 'ni ni-notification-70', color: snackbar.type }"
        :color="snackbar.color"
        :close-handler="closeSnackbar"
      />
    </div>
  </modal-base>
</template>
